/**
 * First-run import: header mapping, the signature normaliser, and the dry-run diff.
 *
 * All pure. Nothing here touches a database, because the number the user is asked to approve —
 * "2,202 songs · 1,323 albums · 837 artists, 176 look like duplicates" — has to be produced by
 * the same code that later does the writing, or the preview is a guess about the import rather
 * than a description of it.
 *
 * Every rule below is measured against a real 2,202-row Exportify export. See
 * docs/IMPORT-PROPOSAL.md; the numbers there come from this module.
 */
import { parseCsv } from "./csv.js";
import { isTrackMarkRatingsHeader, unguardFormula } from "./export.js";
/**
 * Header aliases, so an Exportify file imports with no hand-editing. Matching is
 * case-insensitive and ignores punctuation, which is what makes `Artist Name(s)` and
 * `artist_names` the same header without listing both spellings of everything.
 */
const ALIASES = {
    artist: ["artist names", "artist name", "artists", "artist", "album artist"],
    album: ["album name", "album", "release"],
    track: ["track name", "track", "title", "song", "song name", "name"],
    rating: ["rating", "score", "my rating", "stars"],
    release: ["release date", "released", "year", "date"],
    duration: ["duration ms", "duration", "length ms", "length"],
    genres: ["genres", "genre", "tags"],
    label: ["record label", "label", "publisher"],
    addedAt: ["added at", "date added", "added"],
    /**
     * Exportify writes `Track URI`. It was ignored until listening history arrived, and it is the
     * difference between matching a stream by an id and matching it by a normalised title —
     * an equality rather than a good guess.
     */
    trackUri: ["track uri", "spotify track id", "track id", "uri"],
};
/**
 * Punctuation is DROPPED rather than turned into a separator: Exportify ships `Artist Name(s)`,
 * and replacing the brackets with spaces leaves a stray "s" that matches nothing. Both the header
 * and the alias go through this, so the alias table stays readable.
 */
const flatten = (header) => header.toLowerCase().replace(/[^a-z0-9]/g, "");
/**
 * Header spellings that hold a LIST of artists, and only those.
 *
 * Spotify's column is `Artist Name(s)` and it separates collaborators with a SEMICOLON — measured,
 * not assumed: 148 of 2,261 rows in the real export use `;`, and the two rows containing a comma
 * are band names ("Crosby, Stills & Nash"), not collaborations. So the delimiter is `;`, and a
 * comma is part of a name in this column exactly as it is in every other.
 *
 * The split stays conditioned on the column anyway: a singular `artist` column is this app's own
 * CSV export coming back, and that writes one name per row whatever it contains.
 */
const MULTI_ARTIST = new Set(["artist names", "artists"].map(flatten));
export function isMultiArtistHeader(header) {
    return MULTI_ARTIST.has(flatten(header));
}
/**
 * Maps a header row onto the fields the importer understands.
 *
 * Aliases are tried most-specific first: `Album Name` must win over `Album` where a file has
 * both, and `Track Name` must not be claimed by `album`'s "release" alias. A column already
 * claimed by an earlier field is never reused.
 */
export function mapHeader(header) {
    const flat = header.map(flatten);
    const mapping = {};
    const taken = new Set();
    // Order matters: the fields whose aliases are most likely to be shadowed go first.
    const order = [
        "trackUri",
        "track",
        "artist",
        "album",
        "rating",
        "release",
        "duration",
        "genres",
        "label",
        "addedAt",
    ];
    for (const field of order) {
        for (const alias of ALIASES[field]) {
            const wanted = flatten(alias);
            const index = flat.findIndex((h, i) => h === wanted && !taken.has(i));
            if (index >= 0) {
                mapping[field] = index;
                taken.add(index);
                break;
            }
        }
    }
    return mapping;
}
// ---------------------------------------------------------------- the signature normaliser
/**
 * Words that mark a trailing qualifier as a VARIANT rather than part of the title.
 *
 * The list is deliberately conservative. A qualifier that is not recognised is kept, which means
 * two variants fail to collapse — a missed prompt in the duplicates tray. Getting it wrong the
 * other way destroys a title, and titles are what the user reads.
 */
const VARIANT_WORDS = [
    "remaster",
    "remastered",
    "remastering",
    "live",
    "acoustic",
    "unplugged",
    "demo",
    "edit",
    "radio",
    "mono",
    "stereo",
    "mix",
    "remix",
    "version",
    "session",
    "sessions",
    "instrumental",
    "karaoke",
    "reissue",
    "reprise",
    "rerecorded",
    "take",
    "alternate",
    "extended",
    "single",
    "bonus",
    "anniversary",
    "deluxe",
];
const VARIANT_RE = new RegExp(`\\b(${VARIANT_WORDS.join("|")})\\b`, "i");
/**
 * The same idea for an ALBUM title, which is a different question with a different answer.
 *
 * A song normaliser strips `version`, so `1989 (Taylor's Version) [Deluxe]` collapsed into
 * `1989 (Deluxe Edition)` — two records the user holds separately, merged into one row, one of
 * them losing its identity. A re-recording, a live album and an unplugged set are each their own
 * record; a remaster, a reissue and a deluxe edition are the same record in different packaging,
 * and `album_edition` is where that difference belongs.
 *
 * So this list is the intersection of the song list with "the same album": pressing and edition
 * words only. Everything that marks a different PERFORMANCE is deliberately absent.
 */
const ALBUM_VARIANT_WORDS = [
    "remaster",
    "remastered",
    "remastering",
    "reissue",
    "anniversary",
    "mono",
    "stereo",
    "deluxe",
    "expanded",
    "edition",
    "bonus",
    "version",
];
const ALBUM_VARIANT_RE = new RegExp(`\\b(${ALBUM_VARIANT_WORDS.join("|")})\\b`, "i");
/**
 * Qualifiers that name a different RECORDING, and so are never stripped from an album title even
 * when they also carry an edition word.
 *
 * `version` has to be on the list above — `(Explicit Version)`, `(U.S. Version)` and
 * `(UK Version)` are the same record in different packaging, and leaving them split gives the
 * library the same album twice. But `(Taylor's Version)` is a re-recording, and the possessive is
 * what tells them apart: it says whose performance this is, not how it was pressed.
 */
const ALBUM_KEEP_RE = /['’]s\s+version|\bre-?record(ed|ing)?\b|\blive\b|\bacoustic\b|\bunplugged\b|\bdemo\b|\binstrumental\b|\bkaraoke\b/i;
/**
 * Trailing qualifier forms, in the order a title carries them:
 *   "Song - 2006 Remaster"      Spotify's own convention, the common case
 *   "Song (Live at Wembley)"
 *   "Song [Radio Edit]"
 */
const TRAILING = [/\s+[-–—]\s+([^-–—]+)$/, /\s*\(([^()]*)\)\s*$/, /\s*\[([^[\]]*)\]\s*$/];
/**
 * Strips trailing variant qualifiers from a title, and only those.
 *
 * The obvious rule — cut at the first bracket or dash — destroys 18 real titles in the measured
 * file, because a LEADING parenthetical is part of the name:
 *
 *   naive  "(Don't Fear) The Reaper"                              ->  ""
 *   here   "(Don't Fear) The Reaper"                              ->  "(Don't Fear) The Reaper"
 *   naive  "Cute Without The 'E' (Cut From The Team) - Remastered 2019"  ->  "Cute Without The 'E'"
 *   here   ->  "Cute Without The 'E' (Cut From The Team)"
 *
 * Applied repeatedly, because a title can carry two: "Song - Live - Remastered 2011".
 */
export function stripVariant(title, variants = VARIANT_RE, keep) {
    let out = title.trim();
    for (let pass = 0; pass < 4; pass += 1) {
        let cut = null;
        for (const pattern of TRAILING) {
            const match = out.match(pattern);
            if (match && variants.test(match[1]) && !keep?.test(match[1])) {
                cut = out.slice(0, match.index).trim();
                break;
            }
        }
        // Never strip the whole title: a song genuinely called "Live" keeps its name.
        if (cut === null || cut === "")
            return out;
        out = cut;
    }
    return out;
}
/**
 * `spotify:track:5aAx…` and `https://open.spotify.com/track/5aAx…?si=…` are the same id written
 * two ways. Everything that compares ids goes through this, so a share link pasted into a CSV and
 * a URI out of a streaming export resolve to the same key.
 */
export function trackIdFromUri(uri) {
    if (typeof uri !== "string")
        return null;
    const match = uri.match(/(?:spotify:track:|open\.spotify\.com\/track\/)([A-Za-z0-9]{22})/);
    return match ? match[1] : null;
}
/**
 * The duplicate-detection key. Two rows with the same signature and the same artist are the same
 * song as far as the "same as rated" tray is concerned.
 *
 * A signature collision is a PROMPT, not an error — the measured file has 70 cases where two
 * rows of one album collapse together (a track and its remaster on a deluxe edition), which is
 * why the schema has no `UNIQUE (album_id, signature)`.
 */
export function signature(title) {
    return normalise(stripVariant(title));
}
/**
 * The same key for an album title, stripping only pressing and edition qualifiers.
 *
 * Used everywhere an album is identified — the dry-run diff, the promotion list, and the insert —
 * so the three agree. They must: the promotion key travels to the client and back, and a
 * mismatched normaliser there means a tick that lands on nothing.
 */
export function albumSignature(title) {
    return normalise(stripVariant(title, ALBUM_VARIANT_RE, ALBUM_KEEP_RE));
}
/**
 * The album title as a QUESTION for MusicBrainz — stripped of packaging, still readable.
 *
 * `albumSignature` is the wrong shape for this: it lowercases, folds accents and drops every
 * punctuation mark, which is right for a database key and wrong for a search phrase. What a
 * lookup needs is the same edition-stripping with the title otherwise intact.
 *
 * The reason it needs it at all: an edition qualifier belongs to a RELEASE, and the thing being
 * looked up is a release GROUP. MusicBrainz files Sabrina Carpenter's record as
 * `Man's Best Friend`; the `(Bonus Track Version)` is one of the releases inside it. Searching
 * for the whole string as a phrase asks for a record that does not exist under that name, and
 * the honest answer — "no confident match" — was about the question, not the library.
 */
export function albumSearchTitle(title) {
    return stripVariant(title, ALBUM_VARIANT_RE, ALBUM_KEEP_RE);
}
function normalise(title) {
    return (title
        .normalize("NFKD")
        // Strip combining marks so "Björk" and "Bjork" are one artist, not two.
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/[’']/g, "")
        .replace(/&/g, " and ")
        .replace(/[^a-z0-9]+/g, " ")
        .trim());
}
const cell = (cells, index) => index === undefined ? "" : (cells[index] ?? "").trim();
/**
 * Release dates arrive at mixed granularity — 1,894 full dates, 303 year-only, 5 year-month in
 * the measured file. Only the year is ever displayed, so only the year is kept.
 */
export function releaseYear(value) {
    const match = value.match(/(\d{4})/);
    if (!match)
        return null;
    const year = Number(match[1]);
    return year >= 1850 && year <= 2200 ? year : null;
}
/** A rating outside 1–10 is not clamped — a seed file that disagrees is reported, not corrected. */
export function parseRating(value) {
    if (value === "")
        return { rating: null, error: null };
    const n = Number(value);
    if (!Number.isFinite(n))
        return { rating: null, error: `rating "${value}" is not a number` };
    if (!Number.isInteger(n))
        return { rating: null, error: `rating ${n} is not a whole number` };
    if (n < 1 || n > 10)
        return { rating: null, error: `rating ${n} is outside 1–10` };
    return { rating: n, error: null };
}
export function parseImportFile(text) {
    const table = parseCsv(text);
    const mapping = mapHeader(table.header);
    const rows = [];
    const skipped = [];
    const artistHeader = mapping.artist === undefined ? "" : (table.header[mapping.artist] ?? "");
    const multiArtist = isMultiArtistHeader(artistHeader);
    /*
     * This app's own ratings export escapes a leading `=`, `+`, `-`, `@` or apostrophe so a song
     * title cannot become a formula when the file is opened in a spreadsheet. Reading one back is
     * the only place that escape is undone — applied to any CSV it would eventually meet a real
     * title like `'-Untitled-'` and eat its apostrophe.
     */
    const ours = isTrackMarkRatingsHeader(table.header);
    const unguarded = (value) => (ours ? unguardFormula(value) : value);
    for (const record of table.rows) {
        const raw = {};
        table.header.forEach((name, i) => {
            const value = record.cells[i];
            if (value !== undefined && value !== "")
                raw[name] = value;
        });
        const track = unguarded(cell(record.cells, mapping.track));
        const artist = multiArtist
            ? // Take the first as primary and keep the whole string in `raw`; a featured artist is not a
                // second library artist. Splitting this on `,` filed 140 songs under names like
                // "Misfits;Glenn Danzig" — see migration 0006, which repairs the ones already written.
                unguarded(cell(record.cells, mapping.artist)).split(";")[0].trim()
            : unguarded(cell(record.cells, mapping.artist));
        const album = unguarded(cell(record.cells, mapping.album));
        if (track === "" || artist === "") {
            // Reported in the diff and skipped — never silently dropped. The measured file has one.
            skipped.push({
                line: record.line,
                reason: track === "" && artist === "" ? "no artist or title" : track === "" ? "no title" : "no artist",
                raw,
            });
            continue;
        }
        const { rating, error } = parseRating(cell(record.cells, mapping.rating));
        if (error) {
            skipped.push({ line: record.line, reason: error, raw });
            continue;
        }
        const duration = Number(cell(record.cells, mapping.duration));
        rows.push({
            line: record.line,
            artist,
            // A song whose source names no album is still a song. It becomes a one-song album under
            // its own title rather than being rejected.
            album: album === "" ? track : album,
            track,
            signature: signature(track),
            rating,
            year: releaseYear(cell(record.cells, mapping.release)),
            durationMs: Number.isFinite(duration) && duration > 0 ? Math.round(duration) : null,
            genres: cell(record.cells, mapping.genres)
                .split(",")
                .map((g) => g.trim())
                .filter(Boolean),
            label: cell(record.cells, mapping.label) || null,
            addedAt: cell(record.cells, mapping.addedAt) || null,
            spotifyTrackId: trackIdFromUri(cell(record.cells, mapping.trackUri)),
            raw,
        });
    }
    return { mapping, header: table.header, rows, skipped };
}
const artistKey = (artist) => signature(artist);
const songKey = (artist, sig) => `${artistKey(artist)}|${sig}`;
/**
 * The dry-run. Nothing is written until this has been shown.
 *
 * `existing` is the current library; on a first run it is empty and every row is new. On a
 * re-import it produces the three buckets §J8 requires — new, matched, and matched-with-conflict —
 * because a second import of an overlapping file is the normal case, not an edge case.
 */
export function diffImport(parsed, existing = []) {
    const byKey = new Map();
    for (const song of existing)
        byKey.set(songKey(song.artist, song.signature), song);
    const albums = new Set();
    const artists = new Set();
    const groups = new Map();
    let newRows = 0;
    let matched = 0;
    let ratingsOffered = 0;
    const conflicts = [];
    for (const row of parsed.rows) {
        artists.add(artistKey(row.artist));
        albums.add(`${artistKey(row.artist)}|${albumSignature(row.album)}`);
        const key = songKey(row.artist, row.signature);
        const group = groups.get(key);
        if (group) {
            group.lines.push(row.line);
            group.titles.push(row.track);
        }
        else {
            groups.set(key, { artist: row.artist, signature: row.signature, lines: [row.line], titles: [row.track] });
        }
        const already = byKey.get(key);
        if (!already) {
            newRows += 1;
            if (row.rating !== null)
                ratingsOffered += 1;
            continue;
        }
        matched += 1;
        if (row.rating === null)
            continue;
        if (already.rating === null) {
            ratingsOffered += 1;
        }
        else if (already.rating !== row.rating) {
            conflicts.push({
                line: row.line,
                artist: row.artist,
                title: row.track,
                current: already.rating,
                incoming: row.rating,
            });
        }
    }
    return {
        songs: parsed.rows.length,
        albums: albums.size,
        artists: artists.size,
        skipped: parsed.skipped,
        // Only groups that actually collapse are worth showing; a group of one is just a song.
        duplicates: [...groups.values()].filter((g) => g.lines.length > 1),
        newRows,
        matched,
        conflicts,
        ratingsOffered,
    };
}
/** `|` is a safe separator: both normalisers emit only `[a-z0-9 ]`. */
export const promotionKey = (artist, album) => `${signature(artist)}|${albumSignature(album)}`;
/**
 * Albums the file suggests the user actually owns, rather than has one liked track from.
 *
 * Offered ONCE, as a single reviewable list at the end of import, with the threshold adjustable
 * in that moment — 5 is arbitrary, and seeing what 3+ and 8+ look like before committing is the
 * point (docs/DESIGN-CONFLICTS.md §J5). Not 78 prompts, and never a later nag.
 */
export function promotionCandidates(rows, threshold) {
    const byAlbum = new Map();
    for (const row of rows) {
        const key = `${artistKey(row.artist)}|${albumSignature(row.album)}`;
        const entry = byAlbum.get(key);
        if (entry)
            entry.titles.push(row.track);
        else
            byAlbum.set(key, { artist: row.artist, album: row.album, titles: [row.track] });
    }
    return [...byAlbum.values()]
        .map((a) => ({
        key: promotionKey(a.artist, a.album),
        artist: a.artist,
        album: a.album,
        trackCount: a.titles.length,
        looksLikeSingle: a.titles.length === 1 && signature(a.album) === signature(a.titles[0]),
    }))
        .filter((a) => a.trackCount >= threshold)
        .sort((a, b) => b.trackCount - a.trackCount || a.artist.localeCompare(b.artist));
}
//# sourceMappingURL=import.js.map